<template>
  <base-modal
    :title="'Export project'"
    :width="560"
    @close-modal="onCloseModal"
  >
    <!-- Tabs -->
    <div class="tabs">
      <button
        class="tab"
        :class="{ selected: selectedTab === 'JSON' }"
        @click="selectedTab = 'JSON'"
      >
        JSON
      </button>
      <button
        class="tab"
        :class="{ selected: selectedTab === 'Spreadsheet' }"
        @click="selectedTab = 'Spreadsheet'"
      >
        Spreadsheet
      </button>
      <button
        class="tab"
        :class="{ selected: selectedTab === 'Image' }"
        @click="selectedTab = 'Image'"
      >
        Image
      </button>
      <button
        class="tab"
        :class="{ selected: selectedTab === 'Engine' }"
        @click="selectedTab = 'Engine'"
      >
        Engine
      </button>
      <button
        v-tooltip="pdfTooltipOptions"
        class="tab"
        :class="{
          selected: selectedTab === 'PDF',
          disabled: !isExportPdfSupported,
        }"
        @click="onClickPlan('PDF', isExportPdfSupported)"
      >
        PDF
        <Badge
          v-if="hasBillingPermission && !isExportPdfSupported"
          class="absolute right-5"
          >PRO</Badge
        >
      </button>
      <button
        v-tooltip="backupTooltipOptions"
        class="tab"
        :class="{
          selected: selectedTab === 'Backup',
          disabled: !isImportProjectSupported || !hasBackupExportPermission,
        }"
        :disabled="hasBackupExportPermission ? null : true"
        @click="onClickPlan('Backup', isImportProjectSupported)"
      >
        Backup
        <Badge
          v-if="hasBillingPermission && !isImportProjectSupported"
          class="absolute right-5"
        >
          PRO
        </Badge>
      </button>
    </div>
    <json-tab v-if="selectedTab === 'JSON'" />
    <backup-tab v-if="selectedTab === 'Backup'" />
    <spreadsheet-tab v-if="selectedTab === 'Spreadsheet'" />
    <pdf-tab v-if="selectedTab === 'PDF'" />
    <image-tab v-if="selectedTab === 'Image'" />
    <engine-tab v-if="selectedTab === 'Engine'" />
  </base-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import emitter from 'tiny-emitter/instance';
import { usePremiumFeature } from '@/composables/usePremiumFeature.js';
import { Badge } from '@ui/components/badge';
import { useProtect } from '@/composables/useProtect.js';
import { EVENT, SYS_PERMISSIONS } from '@/constants.js';
import JsonTab from './JsonTab.vue';
import SpreadsheetTab from './SpreadsheetTab.vue';
import ImageTab from './ImageTab.vue';
import PdfTab from './PdfTab.vue';
import EngineTab from './EngineTab.vue';
import BackupTab from './BackupTab.vue';
import BaseModal from '../BaseModal.vue';

export default {
  components: {
    BaseModal,
    JsonTab,
    SpreadsheetTab,
    ImageTab,
    EngineTab,
    PdfTab,
    BackupTab,
    Badge,
  },

  setup() {
    const { hasPermission: hasBillingPermission } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_BILLING_MANAGE],
    });

    const { hasPermission: hasBackupExportPermission } = useProtect({
      permissions: [SYS_PERMISSIONS.SYS_PROJECTS_EXPORT_BACKUP],
    });

    const { isSupported: isExportPdfSupported } = usePremiumFeature({
      feature: 'export_pdf',
    });

    const { isSupported: isImportProjectSupported } = usePremiumFeature({
      feature: 'import_project',
    });

    return {
      isExportPdfSupported,
      isImportProjectSupported,
      hasBillingPermission,
      hasBackupExportPermission,
    };
  },

  data() {
    return {};
  },

  computed: {
    ...mapGetters('project/data/boards', ['getAllNodes']),
    ...mapGetters('project/config/boards', ['getSelections']),
    ...mapGetters('project/config', ['getExportOptions']),

    selectedTab: {
      get() {
        if (
          this.getExportOptions?.selectedTab === 'Backup' &&
          !this.hasBackupExportPermission
        ) {
          return 'JSON';
        }
        return this.getExportOptions?.selectedTab ?? 'JSON';
      },

      set(newValue) {
        this.setExportOptions({
          props: { selectedTab: newValue },
          apply: true,
        });
      },
    },

    rootContainedBoards() {
      const nodes = this.getAllNodes;
      const boards = [];

      nodes.forEach(node => {
        if (!node.children) {
          boards.push(node);
        }
      });

      return boards;
    },

    /**
     * Return what is shown in the tooltip
     * @returns {string}
     */
    pdfTooltipContent() {
      if (!this.hasBillingPermission && !this.isExportPdfSupported) {
        return "This workspace's plan does<br/> not support PDF export";
      }
      return '';
    },

    pdfTooltipOptions() {
      return {
        placement: 'top',
        content: this.pdfTooltipContent,
        html: true,
        distance: 0,
        delay: { show: 0, hide: 0 },
      };
    },

    /**
     * Return what is shown in the tooltip
     * @returns {string}
     */
    backupTooltipContent() {
      if (!this.hasBillingPermission && !this.isImportProjectSupported) {
        return "This workspace's plan does<br/> not support Backup export";
      }
      return '';
    },

    backupTooltipOptions() {
      return {
        placement: 'top',
        content: this.backupTooltipContent,
        html: true,
        offset: 0,
        delay: { show: 0, hide: 0 },
      };
    },
  },

  methods: {
    ...mapActions('application/modals', ['setExportModal']),
    ...mapActions('project/config', ['setExportOptions']),

    onCloseModal() {
      this.setExportModal({ value: false });
      this.loadingClass = '';
      this.isDisabled = false;
      this.exportBoards = 'all';
    },

    /**
     * Checks if the owner has a plan that enables the export
     * @param {String} tab The tab name to be used
     * @param {String} isSupported If the
     */
    onClickPlan(tab, isSupported) {
      if (isSupported) {
        this.selectedTab = tab;
      } else if (this.hasBillingPermission) {
        this.openPlanModal();
      }
    },

    openPlanModal() {
      emitter.emit(EVENT.LIMIT_MODAL, {
        open: true,
        title:
          'The export project feature is not available to your current plan',
        subtitle:
          'Upgrade to any of our plans to unlock export project feature',
      });
    },
  },
};
</script>
<style scoped lang="scss">
:deep(.modal > .content) {
  display: flex;
  background: #242929;
  border-radius: 0 0 10px 10px;
  overflow: hidden;
  .content {
    display: flex;
    flex: 1;
    padding: 30px;
    background: #2c3233;

    form {
      display: flex;
      flex-flow: column;
      flex: 1;
      .fields {
        flex: 0;
        margin: 0;
      }
      .actions {
        display: flex;
        justify-content: flex-end;
        flex-direction: column;
        align-self: end;
        flex: 1;
      }
    }
  }
}

.tabs {
  display: flex;
  flex-flow: column;
  justify-content: space-between;
  background: #242f31;
  align-self: flex-start;
  .tab {
    border: none;
    background: #242929;
    border-left: 3px solid transparent;
    text-align: left;
    font-size: 14px;
    font-weight: 600;
    padding: 20px 50px 20px 17px;
    color: #808b8d;
    position: relative;
    cursor: pointer;
    box-shadow: inset -5px 0px 14px -14px rgb(0 0 0 / 35%);
    &.disabled {
      color: #495659;
      cursor: default;
    }
    &:hover:not(.disabled) {
      color: #fff;
    }
    &.selected {
      border-left: 3px solid #db841e;
      background: #2c3233;
      color: #fff;
      box-shadow: none;
    }
    .upgrade {
      background: #db841e;
      border-radius: 4px;
      color: #fff;
      text-transform: uppercase;
      padding: 4px 6px 4px 6px;
      line-height: 1;
      font-size: 11px;
      font-weight: 600;
      display: inline-block;
      position: absolute;
      right: 20px;
    }
  }
}
</style>
